/**
 * Smoothing for the network-quality indicator.
 *
 * Agora emits `network-quality` every ~2s on a 0..6 scale where 0 means
 * "unknown" (not "down") and 6 means the link is down. Shown straight through,
 * the icon flickers: a single unknown tick blanks it, and 4↔5 oscillation makes
 * it pulse. The smoother holds the last shown level across unknown ticks and
 * only moves to a new level once it has been reported CONFIRM_TICKS times in a
 * row. A reported outage — Agora quality 6, or a `connection-state-change` to
 * DISCONNECTED/RECONNECTING (see the indicator) — drops to no-signal at once.
 */

// Consecutive identical readings required before a level change is shown.
export const CONFIRM_TICKS = 2;
// Consecutive "unknown" (0) readings before the icon drops to no-signal.
export const UNKNOWN_TICKS = 3;
// Distinct from 0: the link is reported down, which must show immediately
// instead of being held like an unknown reading.
export const DOWN = -1;

export interface SmootherState {
  shown: number;
  pending: number;
  pendingTicks: number;
  unknownTicks: number;
}

export function initialSmootherState(shown = 0): SmootherState {
  return { shown, pending: shown, pendingTicks: 0, unknownTicks: 0 };
}

// Agora 1 (excellent) … 5 (very bad) → displayed 5 (excellent) … 1;
// Agora 6 (down) → DOWN; Agora 0 (unknown) → 0, held by the smoother.
export function qualityFromStats(stats: any): number {
  const downlink = Number(stats?.downlinkNetworkQuality) || 0;
  const uplink = Number(stats?.uplinkNetworkQuality) || 0;
  const worst = Math.max(downlink, uplink);
  if (worst === 0) return 0;
  if (worst >= 6) return DOWN;
  return Math.max(0, 6 - worst);
}

export function nextSmootherState(state: SmootherState, reading: number): SmootherState {
  if (reading === DOWN) {
    return initialSmootherState(0);
  }

  if (reading === 0) {
    const unknownTicks = state.unknownTicks + 1;
    if (unknownTicks >= UNKNOWN_TICKS) {
      return { shown: 0, pending: 0, pendingTicks: 0, unknownTicks };
    }
    return { ...state, unknownTicks };
  }

  if (reading === state.shown) {
    return { shown: reading, pending: reading, pendingTicks: 0, unknownTicks: 0 };
  }

  const pendingTicks = reading === state.pending ? state.pendingTicks + 1 : 1;
  // A first real reading (nothing shown yet) is applied at once so the icon
  // appears on the first tick after join instead of CONFIRM_TICKS later.
  if (state.shown === 0 || pendingTicks >= CONFIRM_TICKS) {
    return { shown: reading, pending: reading, pendingTicks: 0, unknownTicks: 0 };
  }
  return { shown: state.shown, pending: reading, pendingTicks, unknownTicks: 0 };
}
